const DEFAULT_BADGE_BASE = {
  color: '#fff',
  padding: '3px 10px',
  borderRadius: '12px',
  fontSize: '11px',
  fontWeight: '600',
  display: 'inline-block',
};

export const CERTIFICATE_HEALTH_META = {
  ACTIVE: { value: 'ACTIVE', label: 'Active', color: '#22c55e', bg: '#f0fdf4', bgIcon: '#dcfce7' },
  EXPIRING_SOON: { value: 'EXPIRING_SOON', label: 'Expiring Soon', color: '#f59e0b', bg: '#fffbeb', bgIcon: '#fef3c7' },
  EXPIRED: { value: 'EXPIRED', label: 'Expired', color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2' },
};

export const CERTIFICATE_LIFECYCLE_META = {
  ACTIVE: { value: 'ACTIVE', label: 'Active', color: '#22c55e', bg: '#f0fdf4', bgIcon: '#dcfce7' },
  SUSPENDED: { value: 'SUSPENDED', label: 'Suspended', color: '#f59e0b', bg: '#fffbeb', bgIcon: '#fef3c7' },
  WITHDRAWN: { value: 'WITHDRAWN', label: 'Withdrawn', color: '#64748b', bg: '#f8fafc', bgIcon: '#f1f5f9' },
  DISCONTINUE_NOT_ACTIVE: { value: 'DISCONTINUE_NOT_ACTIVE', label: 'Discontinue Not Active', color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2' },
  DISCONTINUE_ACTIVE: { value: 'DISCONTINUE_ACTIVE', label: 'Discontinue Active', color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2' },
};

// ISCC shares the ISO widget's shape but under auditqv2's own ISCC-specific status names
// (Valid replaces Active, Terminate replaces Withdrawn) — kept as a separate meta set rather than
// aliasing into CERTIFICATE_LIFECYCLE_META so the two widgets never bleed into each other.
export const CERTIFICATE_LIFECYCLE_ISCC_META = {
  VALID: { value: 'VALID', label: 'Valid', color: '#22c55e', bg: '#f0fdf4', bgIcon: '#dcfce7' },
  SUSPENDED: { value: 'SUSPENDED', label: 'Suspended', color: '#f59e0b', bg: '#fffbeb', bgIcon: '#fef3c7' },
  TERMINATE: { value: 'TERMINATE', label: 'Terminate', color: '#64748b', bg: '#f8fafc', bgIcon: '#f1f5f9' },
  DISCONTINUE_NOT_ACTIVE: { value: 'DISCONTINUE_NOT_ACTIVE', label: 'Discontinue Not Active', color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2' },
  WITHDRAWN: { value: 'WITHDRAWN', label: 'Withdrawn', color: '#64748b', bg: '#f8fafc', bgIcon: '#f1f5f9' },
  DISCONTINUE_ACTIVE: { value: 'DISCONTINUE_ACTIVE', label: 'Discontinue Active', color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2' },
};

export const toCertificateDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeStatusKey = (value) => String(value || '')
  .trim()
  .replace(/([a-z])([A-Z])/g, '$1_$2')
  .replace(/[\s-]+/g, '_')
  .replace(/[^a-zA-Z0-9_]/g, '_')
  .replace(/_+/g, '_')
  .replace(/^_|_$/g, '')
  .toUpperCase();

export const resolveLifecycleMeta = (certificate = {}) => {
  const rawStatus = certificate.lifecycleStatus
    || certificate.certificateLifecycleStatus
    || certificate.auditqLifecycleStatus
    || certificate.auditqStatus
    || certificate.status
    || certificate.certificateStatus
    || '';
  const normalized = normalizeStatusKey(rawStatus);

  if (normalized.includes('DISCONTINUE') && normalized.includes('NOT') && normalized.includes('ACTIVE')) {
    return CERTIFICATE_LIFECYCLE_META.DISCONTINUE_NOT_ACTIVE;
  }
  if (normalized.includes('DISCONTINUE') && normalized.includes('ACTIVE')) {
    return CERTIFICATE_LIFECYCLE_META.DISCONTINUE_ACTIVE;
  }
  if (normalized.includes('WITHDRAW')) return CERTIFICATE_LIFECYCLE_META.WITHDRAWN;
  if (normalized.includes('SUSPEND')) return CERTIFICATE_LIFECYCLE_META.SUSPENDED;
  if (normalized.includes('ACTIVE')) return CERTIFICATE_LIFECYCLE_META.ACTIVE;

  return CERTIFICATE_LIFECYCLE_META.ACTIVE;
};

export const resolveHealthMeta = (certificate = {}, options = {}) => {
  const expiryDate = toCertificateDate(
    certificate.expiryDate
    || certificate.expiredDate
    || certificate.validTo
    || certificate.expireDate
  );

  const now = options.now ? new Date(options.now) : new Date();
  const threshold = new Date(now);
  threshold.setDate(threshold.getDate() + (options.expiringSoonDays || 90));

  if (expiryDate) {
    if (expiryDate < now) return CERTIFICATE_HEALTH_META.EXPIRED;
    if (expiryDate <= threshold) return CERTIFICATE_HEALTH_META.EXPIRING_SOON;
    return CERTIFICATE_HEALTH_META.ACTIVE;
  }

  const normalized = normalizeStatusKey(certificate.healthStatus || certificate.expiryStatus || certificate.status || '');
  if (normalized.includes('EXPIRED')) return CERTIFICATE_HEALTH_META.EXPIRED;
  if (normalized.includes('EXPIRING') || normalized.includes('EXPIR')) return CERTIFICATE_HEALTH_META.EXPIRING_SOON;
  return CERTIFICATE_HEALTH_META.ACTIVE;
};

export const buildBadgeStyle = (meta = CERTIFICATE_HEALTH_META.ACTIVE) => ({
  ...DEFAULT_BADGE_BASE,
  backgroundColor: meta.color,
});

export const resolveStatusMeta = (certificate = {}, options = {}) => {
  const health = resolveHealthMeta(certificate, options);
  return {
    ...health,
    badgeStyle: buildBadgeStyle(health),
    lifecycle: resolveLifecycleMeta(certificate),
    health,
  };
};

// Whether a Certificate ISO/ISCC Status tile should render as "active" (highlighted) given the
// currently applied page filters — requires both the tile's scope (ISO/ISCC) and its exact label
// to match, so e.g. clicking ISO's "Suspended" never also highlights ISCC's "Suspended" tile.
export const isLifecycleTileActive = (tileScope, tileLabel, activeStandard, activeLifecycleStatus) => (
  Boolean(activeStandard) && Boolean(activeLifecycleStatus)
  && activeStandard === tileScope
  && activeLifecycleStatus === tileLabel
);

export const normalizeLifecycleBreakdown = (byLifecycleStatus = {}, metaSet = CERTIFICATE_LIFECYCLE_META) => (
  Object.values(metaSet).map((meta) => ({
    ...meta,
    count: Number(
      byLifecycleStatus[meta.label]
      ?? byLifecycleStatus[meta.value]
      ?? byLifecycleStatus[normalizeStatusKey(meta.label)]
      ?? 0
    ) || 0,
  }))
);

export const buildCertificateLifecycleHealthStats = (stats = {}, certificates = []) => {
  const safeStats = stats || {};
  const health = {
    total: Number(safeStats.total ?? certificates.length ?? 0) || 0,
    active: Number(safeStats.active ?? 0) || 0,
    expiringSoon: Number(safeStats.expiringSoon ?? 0) || 0,
    expired: Number(safeStats.expired ?? 0) || 0,
  };

  if ((!safeStats.total && certificates.length > 0)
      || (health.active + health.expiringSoon + health.expired === 0 && certificates.length > 0)) {
    certificates.forEach((certificate) => {
      const status = resolveHealthMeta(certificate).value;
      if (status === 'EXPIRED') health.expired += 1;
      else if (status === 'EXPIRING_SOON') health.expiringSoon += 1;
      else health.active += 1;
    });
    health.total = certificates.length;
  }

  // byLifecycleStatus is now { iso: {...}, iscc: {...} } (split to mirror auditqv2's two
  // separate dashboard widgets). Older API responses without the split degrade to empty.
  const byLifecycleStatus = safeStats.byLifecycleStatus || {};

  return {
    health,
    lifecycleIso: normalizeLifecycleBreakdown(byLifecycleStatus.iso, CERTIFICATE_LIFECYCLE_META),
    lifecycleIscc: normalizeLifecycleBreakdown(byLifecycleStatus.iscc, CERTIFICATE_LIFECYCLE_ISCC_META),
  };
};
